import type {ReactElement} from 'react';
import React from 'react';
import {ControlledList} from '@Components/controlled-list';
import {getPosterWidthAndHeight, getUnitsFromPosterTypeObject, getWidthAndHeightInPixels, convertPixelsToPosterUnits, PosterTypeUnits} from '@Libraries/poster-object.library';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {BaseCustomSizeOption} from '@Components/download-panel/components/base-custom-size-option/base-custom-size-option';
import {SizeDropdownOptions} from '@Components/download-panel/components/download-design-size-option-wrapper/download-design-size-option-wrapper.types';
import type {PosterLoadObject} from '@PosterWhiteboard/poster/poster.types';
import {isUserPAYG} from '@Libraries/user.library';
import {MAX_FREEBIE_DIMENSION} from '@Libraries/poster-download-library';

interface ImageDesignSizePopupProps {
  getSliderOption: () => ReactElement;
  onClickListItem: (id: SizeDropdownOptions) => void;
  scale: number;
  posterObject: PosterLoadObject;
  selectedDisplaySizeOption: SizeDropdownOptions;
  setSelectedDisplaySizeOption: (newVal: SizeDropdownOptions) => void;
}

export function ImageDesignSizePopup({
  getSliderOption,
  onClickListItem,
  scale,
  posterObject,
  selectedDisplaySizeOption,
  setSelectedDisplaySizeOption,
}: ImageDesignSizePopupProps): ReactElement {
  const getSmallWidthAndHeight = (): {width: number; height: number} => {
    const {width, height} = getPosterWidthAndHeight(posterObject);
    const minDimension = Math.min(width, height);
    let threshold: number;

    switch (getUnitsFromPosterTypeObject(posterObject)) {
      case PosterTypeUnits.CM:
        threshold = 15;
        break;
      case PosterTypeUnits.FT:
        threshold = 0.5;
        break;
      case PosterTypeUnits.MM:
        threshold = 150;
        break;
      case PosterTypeUnits.INCH:
        threshold = 6;
        break;
      case PosterTypeUnits.PX:
      default:
        threshold = 540;
    }

    const scaleFactor = threshold / minDimension;
    return {
      width: Number((width * scaleFactor).toFixed(1)),
      height: Number((height * scaleFactor).toFixed(1)),
    };
  };

  const getLargeWidthAndHeight = (): {width: number; height: number} => {
    const {width, height} = getPosterWidthAndHeight(posterObject);
    const maxDimension = Math.max(width, height);
    let threshold: number;

    switch (getUnitsFromPosterTypeObject(posterObject)) {
      case PosterTypeUnits.CM:
        threshold = 90;
        break;
      case PosterTypeUnits.FT:
        threshold = 4;
        break;
      case PosterTypeUnits.MM:
        threshold = 900;
        break;
      case PosterTypeUnits.INCH:
        threshold = 36;
        break;
      case PosterTypeUnits.PX:
      default:
        threshold = 6000;
    }

    const scaleFactor = threshold / maxDimension;
    return {
      width: Number((width * scaleFactor).toFixed(1)),
      height: Number((height * scaleFactor).toFixed(1)),
    };
  };

  const getOriginalCompressedWidthAndHeight = (): {width: number; height: number} => {
    const {width: pixelWidth, height: pixelHeight} = getWidthAndHeightInPixels(posterObject);
    const maxDimension = Math.max(pixelWidth, pixelHeight);

    if (maxDimension <= MAX_FREEBIE_DIMENSION) {
      return getPosterWidthAndHeight(posterObject);
    }

    const scaleFactor = MAX_FREEBIE_DIMENSION / maxDimension;
    const compressedPixelWidth = pixelWidth * scaleFactor;
    const compressedPixelHeight = pixelHeight * scaleFactor;

    return convertPixelsToPosterUnits(posterObject.type.dpi, posterObject, compressedPixelWidth, compressedPixelHeight);
  };

  const getTextForEachOption = (id: SizeDropdownOptions, forcefullyShowDimensionsWithCustom = false): string => {
    switch (id) {
      case SizeDropdownOptions.ORIGINAL:
        return `${window.i18next.t('pmwjs_original')} · ${getPosterWidthAndHeight(posterObject).width} x ${getPosterWidthAndHeight(posterObject).height}${getUnitsFromPosterTypeObject(posterObject)}`;
      case SizeDropdownOptions.ORIGINAL_COMPRESSED: {
        const compressed = getOriginalCompressedWidthAndHeight();
        return `${window.i18next.t('pmwjs_original_compressed')} · ${compressed.width} x ${compressed.height}${getUnitsFromPosterTypeObject(posterObject)}`;
      }
      case SizeDropdownOptions.SMALL:
        return `${window.i18next.t('pmwjs_small')} · ${getSmallWidthAndHeight().width} x ${getSmallWidthAndHeight().height}${getUnitsFromPosterTypeObject(posterObject)}`;
      case SizeDropdownOptions.LARGE:
        return `${window.i18next.t('pmwjs_large')} · ${getLargeWidthAndHeight().width} x ${getLargeWidthAndHeight().height}${getUnitsFromPosterTypeObject(posterObject)}`;
      case SizeDropdownOptions.CUSTOM:
        return isCustomOptionSelected() || forcefullyShowDimensionsWithCustom
          ? ` ${window.i18next.t('pmwjs_custom')} · ${parseFloat((getPosterWidthAndHeight(posterObject).width * scale).toFixed(2))} x ${parseFloat((getPosterWidthAndHeight(posterObject).height * scale).toFixed(2))}${getUnitsFromPosterTypeObject(posterObject)}`
          : window.i18next.t('pmwjs_custom');
      default:
        return '';
    }
  };

  const isCustomOptionSelected = (): boolean => {
    return selectedDisplaySizeOption === SizeDropdownOptions.CUSTOM;
  };

  const getSizeForOption = (id: SizeDropdownOptions): number => {
    switch (id) {
      case SizeDropdownOptions.SMALL: {
        const small = getSmallWidthAndHeight();
        return small.width * small.height;
      }
      case SizeDropdownOptions.ORIGINAL: {
        const original = getPosterWidthAndHeight(posterObject);
        return original.width * original.height;
      }
      case SizeDropdownOptions.ORIGINAL_COMPRESSED: {
        const compressed = getOriginalCompressedWidthAndHeight();
        return compressed.width * compressed.height;
      }
      case SizeDropdownOptions.LARGE: {
        const large = getLargeWidthAndHeight();
        return large.width * large.height;
      }
      default:
        return 0;
    }
  };

  const getItemsForPopup = (): ControlledListItemType[] => {
    const options: ControlledListItemType[] = [];

    if (isUserPAYG()) {
      const {width, height} = getWidthAndHeightInPixels(posterObject);
      const maxDimension = Math.max(width, height);

      if (maxDimension > MAX_FREEBIE_DIMENSION) {
        options.push({
          id: SizeDropdownOptions.ORIGINAL_COMPRESSED,
          type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
          text: getTextForEachOption(SizeDropdownOptions.ORIGINAL_COMPRESSED),
          isSelected: selectedDisplaySizeOption === SizeDropdownOptions.ORIGINAL_COMPRESSED,
        });
      }
    }

    options.push({
      id: SizeDropdownOptions.ORIGINAL,
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
      text: getTextForEachOption(SizeDropdownOptions.ORIGINAL),
      isSelected: selectedDisplaySizeOption === SizeDropdownOptions.ORIGINAL,
    });

    options.push(
      {
        id: SizeDropdownOptions.SMALL,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        text: getTextForEachOption(SizeDropdownOptions.SMALL),
        isSelected: selectedDisplaySizeOption === SizeDropdownOptions.SMALL,
      },
      {
        id: SizeDropdownOptions.LARGE,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        text: getTextForEachOption(SizeDropdownOptions.LARGE),
        isSelected: selectedDisplaySizeOption === SizeDropdownOptions.LARGE,
      }
    );

    return options.sort((a, b) => {
      const sizeA = getSizeForOption(a.id as SizeDropdownOptions);
      const sizeB = getSizeForOption(b.id as SizeDropdownOptions);
      return sizeA - sizeB;
    });
  };

  const getCustomOptionText = (): string => {
    return isCustomOptionSelected()
      ? ` ${window.i18next.t('pmwjs_custom')} · ${parseFloat((getPosterWidthAndHeight(posterObject).width * scale).toFixed(2))} x ${parseFloat((getPosterWidthAndHeight(posterObject).height * scale).toFixed(2))}${getUnitsFromPosterTypeObject(posterObject)}`
      : window.i18next.t('pmwjs_custom');
  };

  return (
    <div className="flex flex-v-row">
      <ControlledList onClick={onClickListItem} items={getItemsForPopup()} className="spacing-m-0" />
      <BaseCustomSizeOption
        isSelected={isCustomOptionSelected()}
        customText={getCustomOptionText()}
        onClick={(e): void => {
          setSelectedDisplaySizeOption(SizeDropdownOptions.CUSTOM);
          e.stopPropagation();
        }}
        sliderElement={getSliderOption()}
      />
    </div>
  );
}
